import { Info } from 'lucide-react';
import { useState, type CSSProperties, type ReactNode } from 'react';

import { cn } from 'cn';

import type { AgendaSlotOutputType, MateriaOutputType, OcupacaoCelula } from '@shared/dto';

import {
  AdicionarAlunoHorarioForm,
  type AlunoParaMatricula,
  type ProfessorParaMatricula,
} from './adicionar-aluno-horario-form';
import { TIPO_ATENDIMENTO_LABEL } from './aluno-form/enum-labels';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';

export interface ScheduleGridColumn {
  key: string;
  header: ReactNode;
  /** Texto puro (`header` pode ter JSX), pro título do modal. */
  label: string;
}

/** Verde → amarelo → vermelho em 3 faixas fixas conforme a célula se aproxima da capacidade; `undefined` (célula vazia ou sem capacidade) mantém o fundo padrão. */
function estiloOcupacao(ocupacao: OcupacaoCelula): CSSProperties | undefined {
  const total = ocupacao.ocupantes.length + ocupacao.overflow.length;
  if (total === 0 || ocupacao.capacidade <= 0) {
    return undefined;
  }

  const razao = total / ocupacao.capacidade;
  const hue = razao >= 0.8 ? 0 : razao >= 0.5 ? 55 : 142;
  return { backgroundColor: `hsl(${hue} 70% 92%)` };
}

/** Pill com o nome do professor, na mesma cor da pill dele no grid. */
function ProfessorPill({ slot }: { slot: AgendaSlotOutputType }) {
  return (
    <Badge
      className="shrink-0 border-transparent text-white"
      style={{ backgroundColor: slot.professorCorAgenda }}
    >
      {slot.professorNome}
    </Badge>
  );
}

function OcupanteCard({
  slot,
  materias,
}: {
  slot: AgendaSlotOutputType;
  materias: MateriaOutputType[];
}) {
  const materiaNome = materias.find((m) => m.id === slot.materiaId)?.nome ?? '—';
  return (
    <li className="flex items-center gap-3 rounded-xl border p-3">
      <span
        className="flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
        style={{ backgroundColor: slot.professorCorAgenda }}
      >
        {slot.professorNome.charAt(0).toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{slot.alunoNome}</p>
        <p className="truncate text-xs text-muted-foreground">
          {materiaNome}
          {slot.estagio ? ` · ${slot.estagio}` : ''}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <Badge variant="secondary">{TIPO_ATENDIMENTO_LABEL[slot.tipoAtendimento]}</Badge>
        <ProfessorPill slot={slot} />
      </div>
    </li>
  );
}

/** Card de um aluno presente só por spillover (aula anterior ainda não terminou). */
function OverflowCard({ slot }: { slot: AgendaSlotOutputType }) {
  return (
    <li className="flex items-center gap-3 rounded-xl bg-muted/50 p-3">
      <span
        className="flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
        style={{ backgroundColor: slot.professorCorAgenda }}
      >
        {slot.professorNome.charAt(0).toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{slot.alunoNome}</p>
        <p className="text-xs text-muted-foreground">Ainda em sala desde {slot.horario}</p>
      </div>
      <ProfessorPill slot={slot} />
    </li>
  );
}

function ListaOcupacaoCelula({
  ocupacao,
  materias,
}: {
  ocupacao: OcupacaoCelula;
  materias: MateriaOutputType[];
}) {
  if (ocupacao.ocupantes.length === 0 && ocupacao.overflow.length === 0) {
    return <p className="text-sm text-muted-foreground">Nenhum aluno nesse horário.</p>;
  }

  return (
    <div className="space-y-3 text-sm">
      {ocupacao.ocupantes.length > 0 ? (
        <ul className="space-y-2">
          {ocupacao.ocupantes.map((slot) => (
            <OcupanteCard key={slot.horarioId} slot={slot} materias={materias} />
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground">Nenhum aluno começando nesse horário.</p>
      )}

      {ocupacao.overflow.length > 0 ? (
        <div className="space-y-2 border-t border-dashed pt-3">
          <p className="text-xs text-muted-foreground">
            Ainda em sala (aula anterior de 50min, ainda não terminou):
          </p>
          <ul className="space-y-2">
            {ocupacao.overflow.map((slot) => (
              <OverflowCard key={slot.horarioId} slot={slot} />
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function ResumoMatriculaPill({
  slot,
  materias,
}: {
  slot: AgendaSlotOutputType;
  materias: MateriaOutputType[];
}) {
  const materiaNome = materias.find((m) => m.id === slot.materiaId)?.nome ?? '—';
  return (
    <dl className="space-y-1.5 text-xs">
      <p className="text-sm font-semibold text-foreground">{slot.alunoNome}</p>
      <div className="flex justify-between gap-3">
        <dt className="text-muted-foreground">Matéria</dt>
        <dd className="text-right font-medium">{materiaNome}</dd>
      </div>
      <div className="flex justify-between gap-3">
        <dt className="text-muted-foreground">Estágio</dt>
        <dd className="text-right font-medium">{slot.estagio ?? '—'}</dd>
      </div>
      <div className="flex justify-between gap-3">
        <dt className="text-muted-foreground">Atendimento</dt>
        <dd className="text-right font-medium">{TIPO_ATENDIMENTO_LABEL[slot.tipoAtendimento]}</dd>
      </div>
      <div className="flex justify-between gap-3">
        <dt className="text-muted-foreground">Professor</dt>
        <dd className="text-right font-medium">{slot.professorNome}</dd>
      </div>
    </dl>
  );
}

/** Pill de um slot ocupado: badges no fim (nunca encolhem; só o nome trunca); o hover traz um resumo da matrícula. */
function AgendaPill({
  slot,
  materias,
  onClick,
}: {
  slot: AgendaSlotOutputType;
  materias: MateriaOutputType[];
  onClick: (slot: AgendaSlotOutputType) => void;
}) {
  return (
    <HoverCard openDelay={300}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={() => onClick(slot)}
          className="flex items-center gap-1 rounded-md px-2 py-1 text-left text-xs font-medium text-white"
          style={{ backgroundColor: slot.professorCorAgenda }}
        >
          <span className="truncate">{slot.alunoNome}</span>
          <span className="ml-auto flex shrink-0 items-center gap-1">
            {slot.tipoAtendimento === 'PRE_ESCOLAR' ? (
              <span className="rounded-sm bg-white/25 px-1 py-px text-[9px] leading-tight font-semibold whitespace-nowrap">
                30 min
              </span>
            ) : null}
            {slot.alunoConnect ? (
              <span
                className="flex size-3.5 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-[8px] font-bold text-white"
                title="Connect"
              >
                C
              </span>
            ) : null}
            {slot.alunoZonaVermelha ? (
              <span
                className="size-2 shrink-0 rounded-full bg-red-500 ring-1 ring-white"
                title="Zona Vermelha"
              />
            ) : null}
          </span>
        </button>
      </HoverCardTrigger>
      <HoverCardContent className="w-56">
        <ResumoMatriculaPill slot={slot} materias={materias} />
      </HoverCardContent>
    </HoverCard>
  );
}

/** Resumo "X / capacidade" de `modoCelula="ocupacao"`. Sem professor disponível (`capacidade <= 0`) é ausência de atendimento, não vaga: só um traço, sem clique. */
function CelulaOcupacao({
  ocupacao,
  onClick,
  destacada,
}: {
  ocupacao: OcupacaoCelula;
  onClick: () => void;
  /** `false` quando um chip de estado está ativo e a célula não bate: cinza sólido neutro (não opacidade, que deixava a cor real vazar). */
  destacada: boolean;
}) {
  const total = ocupacao.ocupantes.length + ocupacao.overflow.length;

  if (ocupacao.capacidade <= 0) {
    return <p className="py-2 text-center text-muted-foreground">—</p>;
  }

  const lotado = total >= ocupacao.capacidade;
  const vagas = ocupacao.capacidade - total;
  const razao = total / ocupacao.capacidade;

  const cores = !destacada
    ? 'border-slate-300 bg-slate-200 text-slate-400'
    : lotado
      ? 'border-rose-300 bg-rose-50 text-rose-700'
      : total === 0
        ? 'border-slate-200 bg-slate-50 text-slate-500'
        : razao >= 0.5
          ? 'border-amber-200 bg-amber-50 text-amber-700'
          : 'border-emerald-200 bg-emerald-50 text-emerald-700';

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full rounded-lg border px-2 py-2 text-center transition-all hover:shadow-sm hover:-translate-y-px',
        cores,
      )}
      title="Ver detalhes da ocupação"
    >
      <div className="text-sm leading-tight font-semibold">
        {total} / {ocupacao.capacidade}
      </div>
      <div className="mt-0.5 text-[10px] opacity-80">
        {lotado ? 'LOTADO' : `${vagas} vaga${vagas === 1 ? '' : 's'}`}
      </div>
    </button>
  );
}

/** Reaproveitada pela Agenda Geral e pela individual; quem chama decide o que `slotsDaCelula`/`ocupacaoDaCelula` calculam. */
export function ScheduleGrid({
  colunas,
  horarios,
  slotsDaCelula,
  ocupacaoDaCelula,
  materias,
  professores,
  alunos,
  onSlotClick,
  onAlunoAdicionado,
  emDestaque,
  modoCelula = 'pills',
}: {
  colunas: ScheduleGridColumn[];
  horarios: string[];
  /** Só em `modoCelula="pills"`. */
  slotsDaCelula?: (colunaKey: string, horario: string) => AgendaSlotOutputType[];
  /** Ocupação "real" (não filtrada pelos toggles, ao contrário de `slotsDaCelula`), com o spillover de `REGULAR`: base da cor de fundo. */
  ocupacaoDaCelula: (colunaKey: string, horario: string) => OcupacaoCelula;
  /** Só pro nome da matéria no popup de hover da pill -- `AgendaSlotOutputType` só tem o id. */
  materias: MateriaOutputType[];
  /** Elenco completo (não filtrado pela toolbar) pro seletor de professor de "adicionar aluno", sempre pela disponibilidade real. */
  professores: ProfessorParaMatricula[];
  /** Elenco completo de alunos, pro seletor de "adicionar aluno nesse horário". */
  alunos: AlunoParaMatricula[];
  /** Só em `modoCelula="pills"`. */
  onSlotClick?: (slot: AgendaSlotOutputType) => void;
  /** Chamado depois de criar matrícula+horário pelo modal — tipicamente o `refetch()` da tela. */
  onAlunoAdicionado?: () => void;
  /** Células em que retorna `false` perdem a cor de ocupação (viram cinza neutro), pros chips de estado da Agenda Geral. `undefined` não afeta nada. */
  emDestaque?: (colunaKey: string, horario: string) => boolean;
  /** `'pills'` (padrão, Agenda individual) lista cada ocupante; `'ocupacao'` (Agenda Geral) mostra "X / capacidade" e a célula inteira abre o modal. */
  modoCelula?: 'pills' | 'ocupacao';
}) {
  const [celulaInfo, setCelulaInfo] = useState<{
    coluna: ScheduleGridColumn;
    horario: string;
  } | null>(null);

  return (
    <div className="overflow-auto rounded-lg border">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-muted/50">
            <th className="sticky left-0 z-10 min-w-20 bg-muted/50 px-3 py-2 text-left font-medium">
              Horário
            </th>
            {colunas.map((coluna) => (
              <th key={coluna.key} className="min-w-40 border-l px-3 py-2 text-left font-medium">
                {coluna.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {horarios.map((horario) => (
            <tr key={horario} className="border-t">
              <td className="sticky left-0 z-10 bg-background px-3 py-2 font-medium text-muted-foreground">
                {horario}
              </td>
              {colunas.map((coluna) => {
                const ocupacao = ocupacaoDaCelula(coluna.key, horario);
                const temOcupacao = ocupacao.ocupantes.length + ocupacao.overflow.length > 0;
                const destacada = emDestaque ? emDestaque(coluna.key, horario) : true;
                return (
                  <td
                    key={coluna.key}
                    className="group border-l px-2 py-1 align-top"
                    style={modoCelula === 'ocupacao' ? undefined : estiloOcupacao(ocupacao)}
                  >
                    {modoCelula === 'ocupacao' ? (
                      <CelulaOcupacao
                        ocupacao={ocupacao}
                        destacada={destacada}
                        onClick={() => setCelulaInfo({ coluna, horario })}
                      />
                    ) : (
                      <div className="flex flex-col gap-1">
                        {(() => {
                          const slots = slotsDaCelula ? slotsDaCelula(coluna.key, horario) : [];
                          return slots.length === 0 ? (
                            <span className="block px-1 text-muted-foreground">—</span>
                          ) : (
                            slots.map((slot) => (
                              <AgendaPill
                                key={slot.horarioId}
                                slot={slot}
                                materias={materias}
                                onClick={onSlotClick ?? (() => {})}
                              />
                            ))
                          );
                        })()}
                        {/* Espaço reservado pro ícone de info: sempre presente (o hover não causa layout shift) e não `absolute` (nunca flutua sobre uma pill). Clicável mesmo em célula vazia: abre "adicionar aluno". */}
                        <div className="flex h-4 items-center justify-end">
                          <button
                            type="button"
                            onClick={() => setCelulaInfo({ coluna, horario })}
                            className="flex items-center justify-center text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 hover:text-foreground"
                            title={
                              temOcupacao
                                ? 'Ver detalhes da ocupação'
                                : 'Adicionar aluno nesse horário'
                            }
                          >
                            <Info className="size-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <Dialog open={celulaInfo !== null} onOpenChange={(open) => !open && setCelulaInfo(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Ocupação às {celulaInfo?.horario} - {celulaInfo?.coluna.label}
            </DialogTitle>
          </DialogHeader>
          {celulaInfo ? (
            <>
              <ListaOcupacaoCelula
                ocupacao={ocupacaoDaCelula(celulaInfo.coluna.key, celulaInfo.horario)}
                materias={materias}
              />
              <AdicionarAlunoHorarioForm
                diaSemana={celulaInfo.coluna.key}
                horario={celulaInfo.horario}
                professores={professores}
                materias={materias}
                alunos={alunos}
                onCriado={() => onAlunoAdicionado?.()}
              />
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
